/**
 * Defines the shape of a callback function that can be registered for an event.
 * @param data Optional data that can be passed to the callback when the event is emitted.
 */
type EventCallback = (data?: any) => void;

/**
 * A simple implementation of an event emitter (publish-subscribe pattern).
 * This allows for decoupled communication between different parts of the application.
 */
class EventEmitter {
    /**
     * A dictionary to store events and their corresponding listener callbacks.
     * The key is the event name, and the value is an array of callback functions.
     */
    private events: { [key: string]: EventCallback[] } = {};

    /**
     * Registers a callback function to be executed when a specific event is emitted.
     * @param event The name of the event to listen for.
     * @param callback The function to execute when the event is emitted.
     */
    on(event: string, callback: EventCallback) {
        if (!this.events[event]) {
            this.events[event] = [];
        }
        this.events[event].push(callback);
    }

    /**
     * Removes a previously registered callback function for a specific event.
     * @param event The name of the event from which to remove the listener.
     * @param callback The specific callback function to remove.
     */
    off(event: string, callback: EventCallback) {
        if (!this.events[event]) return;
        this.events[event] = this.events[event].filter(cb => cb !== callback);
    }

    /**
     * Dispatches an event, causing all registered callbacks for that event to be executed.
     * @param event The name of the event to emit.
     * @param data Optional data to pass to each callback function.
     */
    emit(event: string, data?: any) {
        if (!this.events[event]) return;
        this.events[event].forEach(callback => callback(data));
    }
}

/**
 * A singleton instance of the EventEmitter.
 * This shared instance is used throughout the application for global event handling,
 * such as opening the cookie consent modal from any component.
 */
export const sharedEventEmitter = new EventEmitter();
